import { Component, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { AppwriteDatabaseService } from '@prism/angular-shared';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppBackgroundOverlayService, BottomSheetService, ConfirmationDialogService, DialogService, SnackbarService } from '@prism/angular-shared';
import { assertNever, BuildInfo, ClientDevice, ClientDeviceType, EnvironmentId, PrismModule, SettingsSubModule } from '@prism/prism-shared';
import { Observable, Subject, catchError, combineLatest, debounceTime, distinctUntilChanged, filter, first, from, interval, map, of, shareReplay, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { buildInfo } from './build-info.const';
import { AppwriteAuthService } from './shared/services/appwrite-auth.service';
import { AutomationApiService } from './shared/services/automation-api.service';
import { DataserverApiService } from './shared/services/dataserver-api.service';
import { ScreenService } from './shared/services/screen.service';
import { SharedApiService } from './shared/services/shared-api.service';
import { UserApiService } from './shared/services/user-api.service';
import { PrismRequestDiagnosticsService } from './shared/services/prism-request-diagnostics.service';
import { CoreActions, CoreSelectors } from './shared/state/core';

@Component({
  selector: 'prism-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'fishbowl';

  public showRefreshSnackbar = false;
  public showRefreshCountdown: boolean = false;
  private timeBeforeRefresh: number = 300;
  public timeRemainingString: string = "5 minutes";

  public deploymentSuffix = buildInfo.deploymentId ? buildInfo.deploymentId.slice(-2) : '';
  public deploymentEnvironmentPrefix = this.getDeploymentEnvironmentPrefix();
  public deploymentTargetPrefix = this.getDeploymentTargetPrefix();
  private unsubscribe$ = new Subject<void>();
  private refreshCountdownStarted = false;
  private readonly buildRefreshRedirectPath = new URL(window.location.href).searchParams.get('prismRedirect');
  private buildRefreshRouteRestoreStarted = false;

  private clientDevice$!: Observable<ClientDevice | null>;

  private currentSubModule: string = ''

  public rightClickMenuVisible = false;
  public rightClickMenuTop = 0;
  public rightClickMenuLeft = 0;

  constructor(
    public screen: ScreenService,
    public dialog: DialogService<any>,
    public bottomSheet: BottomSheetService<any>,
    public auth: AppwriteAuthService,
    public store: Store,
    public confirmationDialog: ConfirmationDialogService,
    public snackbarService: SnackbarService,
    private titleService: Title,
    private router: Router,
    private sharedApi: SharedApiService,
    private dataserverApi: DataserverApiService,
    public appBackgroundOverlayService: AppBackgroundOverlayService,
    public automationApiService: AutomationApiService,
    public appwrite: AppwriteDatabaseService,
    public userApiService: UserApiService,
    public renderer: Renderer2,
    private http: HttpClient,
    private requestDiagnostics: PrismRequestDiagnosticsService,
  ) { }

  ngOnInit(): void {
    this.requestDiagnostics.init();

    this.renderer.listen('document', 'wheel', (e: WheelEvent) => {
      const el = document.activeElement;
      if (el?.tagName !== 'INPUT') return;  // bail instantly in the 99.9% case
      if ((el as HTMLInputElement).type === 'number') {
        (el as HTMLInputElement).blur();
      }
    });
    this.titleService.setTitle(this.getPageTitle('fishbowl'));

    // Set up a listener to update the title on every NavigationEnd event
    this.router.events.pipe(
      //@ts-ignore
      filter((event) => event instanceof NavigationEnd),
      //@ts-ignore
      debounceTime(100),
      tap((event: NavigationEnd) => {
        this.setTitleBasedOnRoute(event.urlAfterRedirects);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();


    this.clientDevice$ = this.dataserverApi.checkIfIAmClientDevice().pipe(
      catchError(() => of(null)),
      shareReplay(1),
    );

    /* THE USERS LIST HANGS OFF AUTH, NOT OFF THE CLIENT-DEVICE PROBE.
       This used to live inside the clientDevice$ pipe below, which meant it could not fire until
       checkIfIAmClientDevice() came back — and that call has no timeout, so when the dataserver
       host drops packets rather than refusing the connection it stalls on the OS connect timeout,
       primary then backup. Everything bound to the users dict (toUserName, toUserImage) renders
       blank for that whole window; browse-purchasing is where it shows, because a comment
       notification deep-links a purchasing agent into an auto-expanded comment thread in a fresh
       tab, so they are looking at names and avatars at the earliest instant of a cold boot.

       Gated on authState rather than dispatched bare, because the user table is read("users") —
       an unauthenticated list is a 401, and app.component.ngOnInit runs ONCE, before a form login
       ever happens. No first(): a kiosk switching users via signInWithToken pushes a new authState
       and needs the dict reloaded, which the old one-shot gate never did. */
    this.auth.authState.pipe(
      filter(authUser => !!authUser),
      distinctUntilChanged((a, b) => a?.uid === b?.uid),
      tap(() => this.store.dispatch(CoreActions.LoadUsers())),
      takeUntil(this.unsubscribe$),
    ).subscribe();

    this.clientDevice$.pipe(
      first(),
      tap((clientDevice) => this.store.dispatch(CoreActions.SetCurrentClientDevice({ clientDevice }))),
      switchMap((clientDevice) => this.resolveAuthenticatedUserId(clientDevice).pipe(
        switchMap((userId) => this.userApiService.getUserById(userId)),
        filter(user => !!user),
        map(user => ({ clientDevice, user })),
      )),
      tap(({ user }) => this.store.dispatch(CoreActions.LoadCurrentUserSuccess({ user }))),
      tap(({ clientDevice }) => {
        if (!this.restoreRouteAfterBuildRefresh() && clientDevice?.defaultLoginUserId) {
          this.router.navigate([clientDevice.defaultUrl]);
        }
      }),
      takeUntil(this.unsubscribe$),
    ).subscribe();

    if (environment.environmentId === EnvironmentId.Production) {
      this.dataserverApi.ping().pipe(
        first(),
        catchError(() => {
          return this.userApiService.checkUserOnNetwork().pipe(
            first(),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            filter(([, currentUser]) => !!currentUser),
            tap(([{ onNetwork }, currentUser]) => {
              if (onNetwork) {
                return;
              }

              const canViewOffNetwork = !!currentUser!.abilities?.[PrismModule.Settings]?.[SettingsSubModule.Display]?.CanViewOffNetwork;

              if (canViewOffNetwork) {
                return;
              }

              from(this.auth.signOut()).pipe(
                first(),
                tap(() => {
                  this.store.dispatch(CoreActions.ClearCurrentUser());
                  this.router.navigate(['/no-access']);
                })
              ).subscribe();

            })
          )
        }),
        takeUntil(this.unsubscribe$),
      ).subscribe();
    }


    // heartbeat every 2 mins
    const clientDeviceUpdateInterval = 1000 * 60 * 2;

    // check if refresh required every 3 mins
    const clientDeviceRefreshCheckInterval = 1000 * 60 * 3;

    combineLatest([
      this.clientDevice$.pipe(
        filter(clientDevice => !!clientDevice),
      ),
      this.store.select(CoreSelectors.getCurrentUser).pipe(
        filter(currentUser => !!currentUser),
      )
    ]).pipe(
      first(),
      map(([device, currentUser]) => {
        const deviceId = device?.id;
        if (!deviceId) {
          return;
        }
        interval(clientDeviceUpdateInterval).pipe(
          startWith(0),
          tap(() => {
            // set heartbeat meta data
            const partial: Partial<ClientDevice> = {
              lastCommunicationTimestamp: new Date(),
              versionTimestamp: buildInfo.deploymentTimestamp,
              loggedInUserId: currentUser!.id,
              softwareVersionCode: 0
            }

            this.automationApiService.updateClientDevice(deviceId, partial).pipe(
              first(),
            ).subscribe();

          }),
          catchError(() => of(null)),
          takeUntil(this.unsubscribe$)
        ).subscribe();

        interval(clientDeviceRefreshCheckInterval).pipe(
          tap(() => {

            this.automationApiService.loadRefreshClientDevices().pipe(
              first(),
              tap((deviceIds) => {
                if (deviceIds.includes(deviceId)) {
                  this.automationApiService.removeRefreshClientDevice(deviceId).pipe(
                    first(),
                    tap(() => {
                      window.location.reload();
                    })
                  ).subscribe()
                }
              }),
              takeUntil(this.unsubscribe$)
            ).subscribe()

          }),
          catchError(() => of(null)),
          takeUntil(this.unsubscribe$),
        ).subscribe()

      }),
      catchError(() => of(null)),
    ).subscribe();


    // Fetch the active deployment's build info from the hosted static asset. If
    // anything looks wrong, leave the browser running instead of forcing reloads.
    interval(3 * 60 * 1000).pipe(
      startWith(0),
      switchMap(() => this.http.get<BuildInfo>(`/assets/build-info.json?t=${Date.now()}`).pipe(
        catchError(() => of(null)),
      )),
      withLatestFrom(
        this.store.select(CoreSelectors.getCurrentUser),
        this.clientDevice$,
      ),
      filter(([remoteBuildInfo, currentUser]) => !!currentUser && this.isDifferentBuild(remoteBuildInfo)),
      tap(([, , clientDevice]) => this.startRefreshCountdown(!!clientDevice)),
      takeUntil(this.unsubscribe$)
    ).subscribe();

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd),
      tap((navEnd) => {
        const urlParts = navEnd.url.split('/').filter(part => !!part)

        if (urlParts[1] !== this.currentSubModule) {
          this.store.dispatch(CoreActions.SetUserSessionData({ userSessionData: {} }))
          this.currentSubModule = urlParts[1] || ''
        }

        this.dialog.closeDialog()
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe()

  }

  // Helper function to set the title based on the URL
  private setTitleBasedOnRoute(url: string): void {
    const urlWithoutQueries = url.split('?')[0]
    const split = urlWithoutQueries.split('/');
    if (url === "/") {
      this.titleService.setTitle(this.getPageTitle('fishbowl'));
      return;
    }

    if (split.length === 2) {
      this.titleService.setTitle(this.getPageTitle(`${split[1]} | fishbowl`));
    } else if (split.length === 3) {
      this.titleService.setTitle(this.getPageTitle(`${split[2]} | fishbowl`));
    }


    // select the body and add a class to it 
    const body = document.querySelector('body') as HTMLBodyElement;

    this.screen.disableScroll$.pipe(
      tap((disableScroll) => {
        if (disableScroll) {
          body.classList.add('disable-scroll');
        } else {
          body.classList.remove('disable-scroll');
        }
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();

    this.screen.disableBackgroundColor$.pipe(
      tap((disableColor) => {
        if (disableColor) {
          body.classList.add('disable-background-color')
        } else {
          body.classList.remove('disable-background-color')
        }
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe()

  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getDeploymentEnvironmentPrefix(): 'TST' | 'STG' | 'PRD' {
    const env = environment.environmentId;
    switch (env) {
      case EnvironmentId.LocalProduction:
      case EnvironmentId.Production:
        return 'PRD';
      case EnvironmentId.LocalStaging:
      case EnvironmentId.Staging:
        return 'STG';
      case EnvironmentId.LocalTesting:
      case EnvironmentId.Testing:
        return 'TST';
      default:
        assertNever(env)
    }
  }

  private getDeploymentTargetPrefix(): string {
    const endpoint = environment.appwrite.endpoint;
    if (endpoint.includes('42.0.0.9')) {
      return 'CLONE 42.0.0.9';
    }

    return '';
  }

  private getPageTitle(baseTitle: string): string {
    if (!this.deploymentTargetPrefix) {
      return baseTitle;
    }

    return `${this.deploymentTargetPrefix} | ${baseTitle}`;
  }

  private resolveAuthenticatedUserId(clientDevice: ClientDevice | null): Observable<string> {
    if (!clientDevice?.defaultLoginUserId) {
      return this.auth.authState.pipe(
        filter(auth => !!auth),
        map(auth => auth!.uid),
        first(),
      );
    }

    return this.auth.authState.pipe(
      first(),
      switchMap((currentUser) => {
        if (currentUser?.uid === clientDevice.defaultLoginUserId) {
          return of(currentUser.uid);
        }

        return this.dataserverApi.getLoginTokenForClientDevice(clientDevice).pipe(
          first(),
          catchError(() => of({ userId: null, secret: null })),
          filter(resp => !!resp?.userId && !!resp?.secret),
          switchMap((resp) => from(this.auth.signInWithToken(resp.userId!, resp.secret!))),
          filter(credential => !!credential?.user),
          map(credential => credential.user!.uid),
        );
      }),
    );
  }

  public refresh() {
    this.reloadWithFreshAppShell();
  }

  private isDifferentBuild(remoteBuildInfo: BuildInfo | null): boolean {
    const localTimestamp = buildInfo.deploymentTimestamp;
    const remoteTimestamp = remoteBuildInfo?.deploymentTimestamp;

    return typeof localTimestamp === 'number' &&
      typeof remoteTimestamp === 'number' &&
      Number.isFinite(localTimestamp) &&
      Number.isFinite(remoteTimestamp) &&
      remoteTimestamp !== localTimestamp;
  }

  private startRefreshCountdown(forceRefresh: boolean): void {
    this.showRefreshSnackbar = true;

    if (!forceRefresh) {
      return;
    }

    if (this.refreshCountdownStarted) {
      return;
    }

    this.refreshCountdownStarted = true;
    this.showRefreshCountdown = true;
    this.timeBeforeRefresh = 300;

    interval(1000).pipe(
      tap(() => {
        this.timeBeforeRefresh--;
        if (this.timeBeforeRefresh <= 0) {
          this.reloadWithFreshAppShell();
        }

        let min = Math.floor(this.timeBeforeRefresh / 60);
        if (min > 0) this.timeRemainingString = `${min} minute${this.timeBeforeRefresh >= 120 ? 's' : ''}`;
        else this.timeRemainingString = `${this.timeBeforeRefresh} second${this.timeBeforeRefresh > 1 ? 's' : ''}`;
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  private reloadWithFreshAppShell(): void {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('prismBuildRefresh');
    currentUrl.searchParams.delete('prismRedirect');

    const redirectPath = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    const url = new URL(window.location.origin);
    url.searchParams.set('prismBuildRefresh', Date.now().toString());
    url.searchParams.set('prismRedirect', redirectPath);
    window.location.replace(url.toString());
  }

  private restoreRouteAfterBuildRefresh(): boolean {
    if (!this.buildRefreshRedirectPath) {
      return false;
    }

    if (!this.buildRefreshRouteRestoreStarted) {
      this.buildRefreshRouteRestoreStarted = true;
      this.router.navigateByUrl(this.buildRefreshRedirectPath, { replaceUrl: true });
    }

    return true;
  }


  @HostListener('document:contextmenu', ['$event'])
  onRightClick(event: MouseEvent) {
    if (!this.screen.customRightClickMenuOptions$.value.length) {
      return;
    }
    event.preventDefault();
    const target = (event.target as HTMLElement).closest('.right-clickable') as HTMLElement;

    if (!target?.id) {
      return;
    }
    this.screen.customRightClickElementId$.next(target.id || '')

    if (target) {
      this.rightClickMenuTop = event.pageY;
      this.rightClickMenuLeft = event.pageX;
      this.rightClickMenuVisible = true;
    }
  }

  @HostListener('document:click')
  onClick() {
    if (!this.screen.customRightClickMenuOptions$.value.length) {
      return;
    }
    this.rightClickMenuVisible = false;
  }

}
